import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card } from "../components/ui";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { UsageMetricRow, metricMeta } from "../components/billing";
import { UsagePacks } from "../components/usage-packs";
import { useAppContext } from "../hooks/useAppContext";
import { api, apiConfigured, type UsageSummary } from "../lib/api";

/**
 * Usage overview (Phase 9, sections 40–41).
 *
 * Per-metric allowance / consumption / remaining, warning thresholds, reset time,
 * and whether each reading is a live ESTIMATE or a FINAL value. Grouped into calm,
 * human categories (BALDER, Integrations, Automation, Storage, Guilds & bots,
 * Exports, Notifications). Upgrade options are offered without pressure. No internal
 * cost or provider data is ever shown — only the server's safe usage projection.
 */

const CATEGORY_ORDER = [
  "BALDER",
  "Automation",
  "Integrations",
  "Notifications",
  "Guilds & bots",
  "Storage",
  "Exports",
  "Other",
] as const;

export function UsageView({ summary }: { summary: UsageSummary }): JSX.Element {
  if (summary.items.length === 0) {
    return <EmptyState title="No usage recorded yet">Usage for the current period will appear here as your community is active.</EmptyState>;
  }
  const byCategory = new Map<string, typeof summary.items>();
  for (const item of summary.items) {
    const cat = metricMeta(item.metric).category;
    const bucket = byCategory.get(cat) ?? [];
    bucket.push(item);
    byCategory.set(cat, bucket);
  }
  const orderedCategories = [...byCategory.keys()].sort(
    (a, b) => CATEGORY_ORDER.indexOf(a as never) - CATEGORY_ORDER.indexOf(b as never),
  );

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <Card title="This billing period">
        <p className="muted">
          Period <span className="mono">{summary.periodKey}</span>. Values marked{" "}
          <strong>Estimated</strong> are still being counted for the current period; values marked{" "}
          <strong>Final</strong> have been reconciled and will not change. Usage never deletes anything —
          it only shows what you have used against your plan.
        </p>
      </Card>

      {orderedCategories.map((category) => (
        <section key={category}>
          <h2 className="section-title">{category}</h2>
          <div className="card-grid">
            {(byCategory.get(category) ?? []).map((item) => (
              <UsageMetricRow key={item.metric} item={item} />
            ))}
          </div>
        </section>
      ))}

      <Card title="Need more headroom?">
        <p className="muted">
          If you are regularly near a limit, you can move to a plan with more allowance whenever it suits
          you. There is no penalty for staying on your current plan, and you will not be charged for going
          over — you will simply see the limit here.
        </p>
        <div className="row" style={{ gap: "0.75rem" }}>
          <Link to="/app/billing/plans">
            <Button variant="secondary">Compare plans</Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: UsageSummary }
  | { kind: "unavailable"; detail?: string };

export function Usage(): JSX.Element {
  const { tenantId, tenantName } = useAppContext();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  const load = useCallback(() => {
    if (!apiConfigured || !tenantId) return;
    setState({ kind: "loading" });
    api.billing
      .usage(tenantId)
      .then((data) => setState({ kind: "ready", data }))
      .catch((error: unknown) =>
        setState({ kind: "unavailable", detail: error instanceof Error ? error.message : undefined }),
      );
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!apiConfigured) {
    return (
      <>
        <PageHeader title="Usage" />
        <NotConfiguredState title="Control API URL not configured">
          Set <code>PUBLIC_API_URL</code> when building the dashboard to view usage.
        </NotConfiguredState>
      </>
    );
  }

  if (!tenantId) {
    return (
      <>
        <PageHeader title="Usage" />
        <EmptyState title="Select a workspace">Choose a workspace to view its usage.</EmptyState>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Usage">
        <p>Current-period usage for {tenantName ?? "this workspace"}, measured against your plan.</p>
      </PageHeader>
      {state.kind === "loading" ? <LoadingState label="Loading usage…" /> : null}
      {state.kind === "unavailable" ? (
        <div className="state" role="alert">
          <ApiUnavailableState detail={state.detail} />
          <Button variant="secondary" onClick={load}>
            Retry
          </Button>
        </div>
      ) : null}
      {state.kind === "ready" ? (
        <div style={{ display: "grid", gap: "1rem" }}>
          <UsageView summary={state.data} />
          <UsagePacks tenantId={tenantId} />
        </div>
      ) : null}
    </>
  );
}
